import React, { FC, ReactNode, useRef, useState, useEffect } from "react";

import styles from "./Demo.module.scss";
import { Container } from "../Container/Container";
import { Appear } from "../Appear/Appear";

export interface CalloutProps {
	headingText?: ReactNode;
	ctaText?: ReactNode;
	ctaHref?: string;
	[key: string]: unknown;
}

export const Demo: FC<CalloutProps> = ({
	// videoUrl = "https://my.adeptly.ai/",
	...attrs
}) => {
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);

	useEffect(() => {
		const v = videoRef.current;
		if (!v) return;

		const onPlay = () => setIsPlaying(true);
		const onPause = () => setIsPlaying(false);
		const onEnded = () => setIsPlaying(false);

		v.addEventListener("play", onPlay);
		v.addEventListener("pause", onPause);
		v.addEventListener("ended", onEnded);

		return () => {
			v.removeEventListener("play", onPlay);
			v.removeEventListener("pause", onPause);
			v.removeEventListener("ended", onEnded);
		};
	}, []);

	// Expose an imperative play helper so other components (e.g. Hero) can trigger the demo.
	useEffect(() => {
		const v = videoRef.current;
		if (!v) return;

		const playDemo = async () => {
			try {
				await v.play();
			} catch (e) {
				// Ignore errors from autoplay policies; user can manually start.
			}
		};

		// Attach to window with a narrowed type
		const win = window as Window & {
			playDemoVideo?: () => Promise<void> | void;
		};
		win.playDemoVideo = playDemo;

		return () => {
			const w = window as Window & {
				playDemoVideo?: () => Promise<void> | void;
			};
			if (w.playDemoVideo === playDemo) {
				delete w.playDemoVideo;
			}
		};
	}, []);

	const handlePlay = async () => {
		const v = videoRef.current;
		if (!v) return;
		try {
			await v.play();
			// state will be updated by the play event listener
		} catch (e) {
			// ignore play errors (autoplay policies), user can click again
		}
	};

	const handleToggle = async () => {
		const v = videoRef.current;
		if (!v) return;
		if (isPlaying) {
			v.pause();
		} else {
			try {
				await v.play();
			} catch (e) {
				// ignore
			}
		}
	};

	return (
		<Appear
			elementType="section"
			aria-labelledby="callout"
			className={styles.wrapper}
			id="demo"
			{...attrs}
		>
			<Container>
				<div className={styles.videoContainerOuter}>
					{/* ensure inner wrapper can contain absolutely positioned overlay */}
					<div className={styles.videoContainerInner}>
						<video
							ref={videoRef}
							controls={false}
							className={styles.video}
							src="/videos/Adeptly_Explainer.mp4"
							poster="/VideoSplash.jpg"
							onClick={handleToggle}
							playsInline
						>
							<track
								id="mytrack"
								label="English Subtitles"
								src="/videos/Adeptly_Explainer.vtt"
								srcLang="en"
								default
							/>
						</video>

						{/* Play overlay - visible when video is not playing */}
						{!isPlaying && (
							<button
								type="button"
								aria-label="Play video"
								onClick={handlePlay}
								className={styles.playOverlayButton}
							>
								<div className={styles.playOverlayButtonCircle}>
									<svg
										width="96"
										height="96"
										viewBox="0 0 96 96"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
										aria-hidden
									>
										<path d="M38 31.5v33l25-16.5-25-16.5z" fill="#fff" />
									</svg>
								</div>
							</button>
						)}
					</div>
				</div>
			</Container>
		</Appear>
	);
};
